import { cx } from '../lib/cx'
import { Button } from './Button'
import { Icon } from './Icon'

/**
 * Reemplazo del `—` en cualquier lugar donde un KPI no tenga valor.
 *
 * Regla de producto: **un KPI sin dato nunca muestra "—" ni cero**. Un guion no
 * distingue "vale cero" de "nadie lo cargó", y un cero es directamente una
 * mentira. Esto dice que falta y quién puede cargarlo: es la palanca contra la
 * deuda de datos que tenía el sheet original.
 *
 * Va en gris y no en rojo: la ausencia de dato no es un crítico de negocio, es
 * una tarea pendiente.
 */
export interface EmptyMetricStateProps {
  /** Nombre de la métrica. Desambigua el botón cuando hay varias vacías. */
  metricNombre: string
  /** Ausente = el rol no puede cargar. Sin handler no hay botón: no ofrecemos
   *  una acción que la API va a rechazar. */
  onLoadData?: () => void
  /** Por qué falta y qué se necesita, ej. "la fuente no está configurada". */
  detalle?: string
  /** Texto del botón. Por defecto "Cargalo acá". */
  textoAccion?: string
  size?: 'sm' | 'md' | 'lg'
  className?: string
}

const VALOR: Record<NonNullable<EmptyMetricStateProps['size']>, string> = {
  sm: 'text-base',
  md: 'text-titulo',
  lg: 'text-seccion',
}

export function EmptyMetricState({
  metricNombre,
  onLoadData,
  detalle,
  textoAccion = 'Cargalo acá',
  size = 'md',
  className,
}: EmptyMetricStateProps) {
  return (
    <div
      data-testid="empty-metric-state"
      className={cx('flex min-w-0 flex-col items-start gap-1.5', className)}
    >
      <span
        className={cx(
          'inline-flex items-center gap-1.5 font-semibold text-muted-strong',
          VALOR[size],
        )}
      >
        <Icon name="sin-dato" size={size === 'lg' ? 16 : 13} />
        Sin dato
      </span>
      {detalle !== undefined ? (
        <span className="text-micro text-muted-strong">{detalle}</span>
      ) : null}
      {onLoadData !== undefined ? (
        <Button variante="chico" onClick={onLoadData} icono="lapiz">
          {textoAccion}
          <span className="sr-only"> — {metricNombre}</span>
        </Button>
      ) : null}
    </div>
  )
}
